// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module StatsCard
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS summary-stat card used across analytics
 * page headers and dashboard compositions. The component combines an optional
 * icon, headline value, and trend indicator into a compact card surface.
 */

import { Badge, Card, CardContent } from "@geenius/ui/solidjs-css";
import { type Component, Show } from "solid-js";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

const SPARKLINE_WIDTH = 120;
const SPARKLINE_HEIGHT = 36;

function getSparklinePoints(values: number[]): string | null {
  const finiteValues = values.filter((value) => Number.isFinite(value));
  if (finiteValues.length < 2) return null;

  const min = Math.min(...finiteValues);
  const max = Math.max(...finiteValues);
  const range = max - min || 1;
  const lastIndex = finiteValues.length - 1;

  return finiteValues
    .map((value, index) => {
      const x = (index / lastIndex) * SPARKLINE_WIDTH;
      const y = SPARKLINE_HEIGHT - ((value - min) / range) * SPARKLINE_HEIGHT;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

/**
 * Props for rendering the SolidJS CSS stats card.
 *
 * @property label Human-readable label for the metric being displayed.
 * @property value Formatted or raw metric value to display prominently.
 * @property icon Optional icon shown beside the metric content.
 * @property change Optional change string rendered below the main value.
 * @property trend Optional direction token used for the change indicator.
 * @property sparkline Optional numeric series rendered as a compact trend line.
 * @property sparklineLabel Accessible label for the sparkline image.
 * @property class Optional class name merged into the card wrapper.
 */
export interface StatsCardProps {
  label: string;
  value: string | number;
  icon?: string;
  change?: string;
  trend?: "up" | "down" | "neutral";
  sparkline?: number[];
  sparklineLabel?: string;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders the SolidJS CSS summary-stat card.
 *
 * @param props Metric label, value, and optional trend presentation props.
 * @returns A card element showing the supplied summary metric.
 */
export const StatsCard: Component<StatsCardProps> = (props) => {
  const trendLabel = () =>
    props.trend === "up"
      ? "increased"
      : props.trend === "down"
        ? "decreased"
        : "changed";
  const sparklinePoints = () => getSparklinePoints(props.sparkline ?? []);

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardContent class="gn-analytics-stats-card" data-analytics-stat>
          <ErrorState
            error={props.error}
            title={props.label}
            onRetry={props.onRetry}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card class={props.class}>
      <CardContent class="gn-analytics-stats-card" data-analytics-stat>
        <Show when={props.icon}>
          <div
            class="gn-analytics-stats-card__icon"
            data-analytics-stat-icon
            aria-hidden="true"
          >
            {props.icon}
          </div>
        </Show>
        <div
          class="gn-analytics-stats-card__content"
          data-analytics-stat-content
        >
          <span
            class="gn-analytics-stats-card__label"
            data-analytics-stat-label
          >
            {props.label}
          </span>
          <span
            class="gn-analytics-stats-card__value"
            data-analytics-stat-value
          >
            {props.value}
          </span>
          <Show when={props.change}>
            <Badge
              size="sm"
              variant={
                props.trend === "up"
                  ? "success"
                  : props.trend === "down"
                    ? "danger"
                    : "secondary"
              }
            >
              <span
                class="gn-analytics-stats-card__change"
                data-analytics-stat-change
                data-trend={props.trend || "neutral"}
                role="img"
                aria-label={`${trendLabel()} ${props.change}`}
              >
                <span aria-hidden="true">
                  {props.trend === "up"
                    ? "↑"
                    : props.trend === "down"
                      ? "↓"
                      : ""}
                </span>{" "}
                {props.change}
              </span>
            </Badge>
          </Show>
          <Show when={sparklinePoints()}>
            <svg
              class="gn-analytics-stats-card__sparkline"
              data-analytics-stat-sparkline
              viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
              role={props.sparklineLabel ? "img" : undefined}
              aria-label={props.sparklineLabel}
              aria-hidden={props.sparklineLabel ? undefined : true}
            >
              <polyline
                class="gn-analytics-stats-card__sparkline-line"
                points={sparklinePoints() ?? ""}
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </Show>
        </div>
      </CardContent>
    </Card>
  );
};
